import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import $ from "jquery";

gsap.registerPlugin(ScrollTrigger);

function Cart() {
  const [cartId] = useState(localStorage.getItem("cart_id"));
  const [cart, setCart] = useState(
    JSON.parse(localStorage.getItem("cart")) || [],
  );
  const [totalAmount, setTotalAmount] = useState(
    parseFloat(localStorage.getItem("totalAmount")) || 0,
  );
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [randomProducts, setRandomProducts] = useState([]);
  const [loadingRandom, setLoadingRandom] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
   const [updatingItemId, setUpdatingItemId] = useState(null); // ✅ Added this line
  const navigate = useNavigate();

  const toastRef = useRef(null);
  const clearConfirmRef = useRef(null);
  const couponInputRef = useRef(null);

  const FALLBACK_IMG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
      <rect width="100%" height="100%" fill="#f2f2f2"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial" font-size="18" fill="#777">No Image</text>
    </svg>
  `);

  const formatINR = (n) => `₹${Number(n || 0).toFixed(2)}`;
  const formatCouponOffer = (coupon) => {
    if (!coupon) return "";

    const discountValue = Number(coupon.discount_value || 0);
    const minOrderAmount = Number(coupon.min_order_amount || 0);
    const maxDiscountAmount = Number(coupon.max_discount_amount || 0);
    const isPercentage = coupon.discount_type === "percentage";
    const offer = isPercentage
      ? `${discountValue}% off`
      : `${formatINR(discountValue)} off`;
    const limit = isPercentage && maxDiscountAmount > 0
      ? ` up to ${formatINR(maxDiscountAmount)}`
      : "";
    const minimum = minOrderAmount > 0
      ? ` on orders above ${formatINR(minOrderAmount)}`
      : "";

    return `${offer}${limit}${minimum}`;
  };

  const getProductTitle = (p) =>
    p?.name || p?.product_name || p?.productName || "Product";

  const joinUrl = (base, path) => {
    if (!path) return "";
    if (typeof path !== "string") return "";
    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:")
    )
      return path;
    const b = String(base || "").replace(/\/+$/, "");
    const pth = path.replace(/^\/+/, "");
    if (!b) return `/${pth}`;
    return `${b}/${pth}`;
  };

  const getUnitPrice = (product) => {
    const price = Number(
      product?.price ?? product?.original_price ?? product?.product_price ?? 0,
    );
    const discount = Number(
      product?.discount_price ??
        product?.discounted_price ??
        product?.discount ??
        0,
    );
    if (discount && !Number.isNaN(discount) && discount > 0 && discount < price)
      return discount;
    return price;
  };

  const getLineTotal = (product) => {
    const unit = getUnitPrice(product);
    return Number(unit) * Number(product?.quantity || 1);
  };

  const getSelectionLabel = (product) => {
    const value = product?.size || product?.purity || "N/A";
    return Number(product?.is_purity_price) === 1 ? `Purity: ${value}` : `Size: ${value}`;
  };
// ✅ NEW: Check if ANY item in the cart has bulk discount (not just selected)
const hasBulkDiscountInCart = () => {
  return cart.some(item => hasBulkDiscount(item));
};

// ✅ FIXED: Helper function to check if a product has ACTIVE bulk discounts based on CURRENT quantity
// ✅ CORRECTED: Helper function to check if a product has ACTIVE bulk discounts
// ✅ CORRECTED: Helper function to check if a product has ACTIVE bulk discounts
const hasBulkDiscount = (product) => {
  if (!product) return false;
  
  const quantity = product?.quantity || 1;
  
  // Only consider it a bulk discount if discount_type is explicitly 'bulk'
  if (product.discount_type === 'bulk') {
    // Check if we have min_quantity and quantity meets it
    if (product.applied_min_quantity) {
      return quantity >= product.applied_min_quantity;
    }
    // If discount_type is 'bulk' but no min_quantity, assume it's active
    // (but this should be fixed in backend)
    return true;
  }
  
  // If discount_type is NOT 'bulk', it's NOT a bulk discount
  // Even if applied_min_quantity exists, it might be from single discount
  return false;
};

  // ✅ NEW: Helper function to check if ANY selected item has bulk discount
  const hasBulkDiscountInSelectedItems = () => {
    const selectedItemsList = cart.filter(item => selectedItems.includes(item.cart_item_id));
    return selectedItemsList.some(item => hasBulkDiscount(item));
  };

  const showMessage = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("info");
    }, 3000);
  };

  const getProductImage = (product) => {
    if (
      product?.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const first = product.images[0];
      const candidate =
        first?.url || first?.filename || first?.image_url || first;
      const url = joinUrl(imgLocation, candidate);
      return url || FALLBACK_IMG;
    }
    if (product?.image) {
      const url = joinUrl(imgLocation, product.image);
      return url || FALLBACK_IMG;
    }
    const raw = product?.img_path || product?.image_path;
    if (!raw) return FALLBACK_IMG;
    if (
      typeof raw === "string" &&
      (raw.startsWith("http://") ||
        raw.startsWith("https://") ||
        raw.startsWith("data:"))
    )
      return raw;
    if (typeof raw === "string") {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          const firstImage = String(arr[0] || "").trim();
          const url = joinUrl(imgLocation, firstImage);
          return url || FALLBACK_IMG;
        }
      } catch (e) {}
      const parts = raw
        .split(",")
        .map((p) => String(p || "").trim())
        .filter(Boolean);
      if (parts.length > 0) {
        const url = joinUrl(imgLocation, parts[0]);
        return url || FALLBACK_IMG;
      }
      const url = joinUrl(imgLocation, raw);
      return url || FALLBACK_IMG;
    }
    return FALLBACK_IMG;
  };

  const fetchAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await $.getJSON(`${ApiURl}/getCoupons.php`);
      if (response?.success) setAvailableCoupons(response.coupons || []);
      else setAvailableCoupons([]);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setAvailableCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

// ✅ FIXED: applyCoupon function with proper bulk discount check
const applyCoupon = async () => {
  if (!couponCode.trim()) {
    showMessage("Please enter a coupon code", "warning");
    return;
  }
  
  if (selectedCartItems.length === 0) {
    showMessage("Please select items to apply coupon", "warning");
    return;
  }
  
  // ✅ CRITICAL: Check if ANY item in cart has bulk discount
  const hasBulkInCart = cart.some(item => hasBulkDiscount(item));
  if (hasBulkInCart) {
    showMessage("Coupons cannot be applied when products have bulk discounts. Please remove or adjust quantity of bulk discounted items.", "warning");
    return;
  }
  
  const userId = localStorage.getItem("user_id");
  if (!userId) {
    showMessage("Please login to apply coupon", "warning");
    return;
  }
  
  setIsApplyingCoupon(true);
  try {
    const response = await fetch(`${ApiURl}/applyCoupon.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: couponCode.toUpperCase().trim(),
        user_id: userId,
        order_amount: selectedTotalAmount,
      }),
    });
    
    const data = await response.json();
    
    if (data?.success) {
      setAppliedCoupon(data.coupon);
      setCouponDiscount(data.discount);
      setShowCouponInput(false);
      showMessage(
        `Coupon applied! You saved ${formatINR(data.discount)}`,
        "success",
      );
      localStorage.setItem(
        "applied_coupon",
        JSON.stringify({
          id: data.coupon.id,
          code: data.coupon.code,
          discount: data.discount,
          description: data.coupon.description || "",
        }),
      );
    } else {
      showMessage(data?.message || "Invalid coupon code", "error");
      setAppliedCoupon(null);
      setCouponDiscount(0);
      localStorage.removeItem("applied_coupon");
    }
  } catch (error) {
    console.error("Error applying coupon:", error);
    showMessage("Error applying coupon", "error");
  } finally {
    setIsApplyingCoupon(false);
  }
};

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode("");
    localStorage.removeItem("applied_coupon");
    showMessage("Coupon removed", "info");
  };
  
useEffect(() => {
  if (!cart.length) return;
  
  
  
  const hasBulk = cart.some(item => hasBulkDiscount(item));
  
  if (hasBulk && appliedCoupon) {
    removeCoupon();
    showMessage("Coupon removed because bulk discount is active", "info");
  }
}, [cart, appliedCoupon]);

  useEffect(() => {
    if (!message || !toastRef.current) return;
    gsap.fromTo(
      toastRef.current,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.4, ease: "back.out(1.7)" },
    );
  }, [message]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clearConfirmRef.current &&
        !clearConfirmRef.current.contains(event.target)
      )
        setShowClearConfirm(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const savedCoupon = localStorage.getItem("applied_coupon");
    if (savedCoupon) {
      try {
        const parsed = JSON.parse(savedCoupon);
        setAppliedCoupon(parsed);
        setCouponDiscount(parsed.discount);
      } catch (error) {
        console.error("Error loading saved coupon:", error);
      }
    }
  }, []);

  useEffect(() => {
  const fetchCartItems = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${ApiURl}/getCartItems.php?cart_id=${cartId}`,
      );
      const data = await response.json();
    
      if (data?.data) {
        setCart(data.data);
        const total = data.data.reduce(
          (acc, item) => acc + getLineTotal(item),
          0,
        );
        setTotalAmount(total);
        const selectedItemsFromDb = data.data
          .filter((item) => item.selected === 1)
          .map((item) => item.cart_item_id);
        setSelectedItems(selectedItemsFromDb);
        localStorage.setItem("cart", JSON.stringify(data.data));
        localStorage.setItem("totalAmount", total);
        window.dispatchEvent(new Event("storage"));
        if (data.data.length > 0) fetchRandomProducts(data.data);
      } else {
        if (cart.length > 0) fetchRandomProducts(cart);
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      showMessage("Error loading cart items", "error");
      if (cart.length > 0) fetchRandomProducts(cart);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (cartId) {
    fetchCartItems();
  } else {
    setIsLoading(false);
    if (cart.length > 0) fetchRandomProducts(cart);
  }
  fetchAvailableCoupons();
}, [cartId]);

  const fetchRandomProducts = async (currentCart = cart) => {
    setLoadingRandom(true);
    setRandomProducts([]);
    try {
      const response = await fetch(`${ApiURl}/getallproducts.php`);
      const data = await response.json();
      let products = [];
      if (data?.products && Array.isArray(data.products))
        products = data.products;
      else if (data?.data && Array.isArray(data.data)) products = data.data;
      else if (Array.isArray(data)) products = data;
      else if (data?.result && Array.isArray(data.result))
        products = data.result;
      if (products.length > 0) {
        const cartProductIds = (currentCart || [])
          .map((item) => {
            const id = item?.product_id || item?.id;
            return id ? String(id) : null;
          })
          .filter(Boolean);
        const availableProducts = products.filter((product) => {
          const productId = product?.product_id || product?.id;
          return productId && !cartProductIds.includes(String(productId));
        });
        if (availableProducts.length > 0) {
          const shuffled = [...availableProducts].sort(
            () => 0.5 - Math.random(),
          );
          const selected = shuffled.slice(0, 20);
          setRandomProducts(selected);
        } else {
          setRandomProducts([]);
        }
      } else {
        setRandomProducts([]);
      }
    } catch (error) {
      console.error("Error fetching random products:", error);
      setRandomProducts([]);
    } finally {
      setLoadingRandom(false);
    }
  };

  const addToWishlist = async (productId) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      showMessage("Please login to add to wishlist", "warning");
      return;
    }
    try {
      const response = await fetch(`${ApiURl}/addWishlistItem.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, product_id: productId }),
      });
      const data = await response.json();
      showMessage(data.message || "Added to wishlist", "success");
      window.dispatchEvent(new Event("storage"));
      localStorage.setItem("wishlist_updated", Date.now());
    } catch (error) {
      console.error("Error adding item to wishlist:", error);
      showMessage("Error adding item to wishlist", "error");
    }
  };

// ✅ FIXED: handleQuantityChange with proper bulk discount status update
const handleQuantityChange = async (cartItemId, quantity) => {
  if (quantity === "") {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.cart_item_id === cartItemId ? { ...item, quantity: "" } : item,
      ),
    );
    return;
  }
  
  const parsedQuantity = parseInt(quantity, 10);
  if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
    const current = cart.find((item) => item.cart_item_id === cartItemId);
    const unit = getUnitPrice(current);
    const updatedTotal = Number(unit || 0) * 1;
    try {
      const response = await fetch(`${ApiURl}/updateCartItem.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart_item_id: cartItemId,
          quantity: 1,
          total: updatedTotal,
        }),
      });
      const result = await response.json();
     if (result.success) {
  // Update cart with server response data IMMEDIATELY
  setCart(prevCart => {
    const updatedCart = prevCart.map((item) =>
      item.cart_item_id === cartItemId
        ? {
            ...item,
            quantity: parsedQuantity,
            price: result.discounted_price ?? result.original_price ?? item.price,
            original_price: result.original_price ?? item.original_price, // Make sure this is set
            discount_type: result.discount_type ?? item.discount_type,   // This should be 'bulk'
            discount_value: result.discount_value ?? item.discount_value, // This should be 200
            applied_min_quantity: result.applied_min_quantity ?? item.applied_min_quantity,
            total: result.new_total ?? updatedTotal,
          }
        : item,
    );
    
    return updatedCart;
  });
  
  // Update total amount
  setTimeout(() => {
    setCart(currentCart => {
      const newTotal = currentCart.reduce((acc, item) => acc + getLineTotal(item), 0);
      setTotalAmount(newTotal);
      localStorage.setItem("cart", JSON.stringify(currentCart));
      localStorage.setItem("totalAmount", newTotal);
      window.dispatchEvent(new Event("storage"));
      return currentCart;
    });
  }, 0);
  
  showMessage("Quantity updated", "success");
} else {
        showMessage(result.message || "Failed to update quantity", "error");
      }
    } catch (error) {
      console.error("Error updating cart item:", error);
      showMessage("Error updating quantity", "error");
    }
    return;
  }
  
  const current = cart.find((item) => item.cart_item_id === cartItemId);
  const unit = getUnitPrice(current);
  const updatedTotal = Number(unit || 0) * parsedQuantity;
  
  try {
    setUpdatingItemId(cartItemId);
    
    const response = await fetch(`${ApiURl}/updateCartItem.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cart_item_id: cartItemId,
        quantity: parsedQuantity,
        total: updatedTotal,
      }),
    });
    
    const result = await response.json();
    
    if (result.success) {
  // Update cart with server response data IMMEDIATELY
  setCart(prevCart => {
    const updatedCart = prevCart.map((item) =>
      item.cart_item_id === cartItemId
        ? {
            ...item,
            quantity: parsedQuantity,
            price: result.discounted_price ?? result.original_price ?? item.price,
            original_price: result.original_price ?? item.original_price, // Make sure this is set
            discount_type: result.discount_type ?? item.discount_type,   // This should be 'bulk'
            discount_value: result.discount_value ?? item.discount_value, // This should be 200
            applied_min_quantity: result.applied_min_quantity ?? item.applied_min_quantity,
            total: result.new_total ?? updatedTotal,
          }
        : item,
    );
    
    return updatedCart;
  });
  
  // Update total amount
  setTimeout(() => {
    setCart(currentCart => {
      const newTotal = currentCart.reduce((acc, item) => acc + getLineTotal(item), 0);
      setTotalAmount(newTotal);
      localStorage.setItem("cart", JSON.stringify(currentCart));
      localStorage.setItem("totalAmount", newTotal);
      window.dispatchEvent(new Event("storage"));
      return currentCart;
    });
  }, 0);
  
  showMessage("Quantity updated", "success");
}else {
      if (result.message && result.message.includes("stock")) {
        showMessage(result.message, "error");
      } else {
        showMessage(result.message || "Failed to update quantity", "error");
      }
    }
  } catch (error) {
    console.error("Error updating quantity:", error);
    showMessage("Error updating quantity. Please try again.", "error");
  } finally {
    setUpdatingItemId(null);
  }
};

  const handleQuantityBlur = async (cartItemId, currentValue) => {
    if (currentValue === "" || currentValue === "0")
      handleQuantityChange(cartItemId, 1);
  };

  const removeCartItem = async (cartItemId) => {
    try {
      const response = await fetch(`${ApiURl}/removeCartItem.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cart_item_id: cartItemId }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const updatedCart = cart.filter(
            (item) => item.cart_item_id !== cartItemId,
          );
          setCart(updatedCart);
          const updatedTotalAmount = updatedCart.reduce(
            (acc, item) => acc + getLineTotal(item),
            0,
          );
          setTotalAmount(updatedTotalAmount);
          setSelectedItems((prev) => prev.filter((id) => id !== cartItemId));
          if (appliedCoupon) removeCoupon();
          localStorage.setItem("cart", JSON.stringify(updatedCart));
          localStorage.setItem("totalAmount", updatedTotalAmount);
          window.dispatchEvent(new Event("storage"));
          showMessage("Item removed from cart", "success");
          if (updatedCart.length > 0) fetchRandomProducts(updatedCart);
          else setRandomProducts([]);
        } else {
          showMessage(result.message || "Failed to remove item", "error");
        }
      } else {
        showMessage("Failed to remove item", "error");
      }
    } catch (error) {
      console.error("Error removing item:", error);
      showMessage("Error removing item", "error");
    }
  };

  const handleSelectItem = async (cartItemId) => {
    const isSelected = selectedItems.includes(cartItemId);
    const newSelectedItems = isSelected
      ? selectedItems.filter((id) => id !== cartItemId)
      : [...selectedItems, cartItemId];
    setSelectedItems(newSelectedItems);
    if (appliedCoupon) removeCoupon();
    try {
      const response = await fetch(`${ApiURl}/updateCartSelection.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart_item_id: cartItemId,
          selected: isSelected ? 0 : 1,
        }),
      });
      const result = await response.json();
      if (!result.success)
        console.error("Error updating selection in database:", result.message);
    } catch (error) {
      console.error("Error updating item selection:", error);
    }
  };

  const clearCart = async () => {
    setShowClearConfirm(false);
    if (!cartId || cart.length === 0) {
      showMessage("Cart is already empty", "info");
      return;
    }
    try {
      const cartItemIds = cart.map((item) => item.cart_item_id);
      const removePromises = cartItemIds.map((cartItemId) =>
        fetch(`${ApiURl}/removeCartItem.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_item_id: cartItemId }),
        }),
      );
      const responses = await Promise.all(removePromises);
      const allSuccessful = responses.every((response) => response.ok);
      if (allSuccessful) {
        setCart([]);
        setSelectedItems([]);
        setTotalAmount(0);
        setRandomProducts([]);
        removeCoupon();
        localStorage.setItem("cart", JSON.stringify([]));
        localStorage.setItem("totalAmount", "0");
        window.dispatchEvent(new Event("storage"));
        showMessage("Cart cleared successfully", "success");
      } else {
        showMessage("Failed to clear some items", "error");
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      setCart([]);
      setSelectedItems([]);
      setTotalAmount(0);
      setRandomProducts([]);
      removeCoupon();
      localStorage.setItem("cart", JSON.stringify([]));
      localStorage.setItem("totalAmount", "0");
      window.dispatchEvent(new Event("storage"));
      showMessage("Cart cleared locally", "warning");
    }
  };

  const handleProceedToCheckout = () => {
  const selectedCartItems = cart.filter((item) =>
    selectedItems.includes(item.cart_item_id),
  );
  
  if (selectedCartItems.length === 0) {
    showMessage(
      "Please select at least one item to proceed to checkout.",
      "warning",
    );
    return;
  }
  
  const finalAmount = selectedTotalAmount - couponDiscount;
  
  // Create the checkout data with all discount information
  const checkoutData = {
    user_id: localStorage.getItem("user_id"),
    total: finalAmount,
    items: selectedCartItems.map((item) => ({
      cart_item_id: item.cart_item_id,
      product_id: item.product_id,
      product_price_id: item.product_price_id,
      name: item.name,
      product_name: item.product_name,
      img_path: item.img_path,
      image: item.image,
      image_url: item.image_url,
      image_path: item.image_path,
      images: item.images,
      purity: item.purity,
      metal: item.metal,
      quantity: item.quantity,
      price: item.price,
      original_price: item.original_price || item.price,
      discount_type: item.discount_type || '',
      discount_value: item.discount_value || 0,
      total: getLineTotal(item),
      size: item.size,
      is_purity_price: item.is_purity_price,
      sgst: item.sgst || 0,
      cgst: item.cgst || 0,
      igst: item.igst || 0,
      selected: 1
    })),
    address: {
      full_name: "", // You need to get this from your address form/state
      phone: "", // You need to get this from your address form/state
      address_line1: "",
      address_line2: "",
      landmark: "",
      locality: "",
      city: "",
      state: "",
      pincode: ""
    },
    coupon: appliedCoupon ? {
      id: appliedCoupon.id,
      code: appliedCoupon.code,
      discount: couponDiscount,
      description: appliedCoupon.description || "",
    } : null,
    coupon_id: appliedCoupon?.id || 0,
    coupon_code: appliedCoupon?.code || '',
    coupon_discount: couponDiscount || 0
  };
  

  
  // Store in localStorage for the checkout page
  localStorage.setItem("checkout_data", JSON.stringify(checkoutData));
  localStorage.setItem("checkout_items", JSON.stringify(selectedCartItems));
  localStorage.setItem("checkout_total", finalAmount.toString());
  
  // Navigate to checkout page
  navigate("/cart/checkout");
};

  const viewProductDetails = (productId) => {
    if (!productId) return;
    navigate(`/ProductOverview/${productId}`);
  };

  const selectedCartItems = cart.filter((item) =>
    selectedItems.includes(item.cart_item_id),
  );
  const selectedTotalAmount = selectedCartItems.reduce(
    (acc, item) => acc + getLineTotal(item),
    0,
  );
  const finalAmount = selectedTotalAmount - couponDiscount;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    hover: { y: -4, transition: { duration: 0.2, ease: "easeInOut" } },
  };

  const LoadingState = () => (
    <div className="rounded-2xl border border-[#b29063]/20 bg-white p-10 text-center shadow-sm">
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="relative">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#b29063] border-r-transparent"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-6 w-6 rounded-full bg-[#b29063]/10"></div>
          </div>
        </div>
        <p className="text-sm font-medium text-gray-600">
          Loading your cart...
        </p>
      </div>
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-[#b29063]/20 bg-white p-8 sm:p-12 text-center shadow-sm"
    >
      <motion.div
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#b29063]/10 to-[#b29063]/20 text-3xl"
      >
        <svg
          className="h-10 w-10 text-[#b29063]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </motion.div>
      <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
        Your shopping cart is empty
      </h2>
      <p className="mt-2 text-gray-600 sm:text-base">
        Browse products and tap{" "}
        <span className="font-semibold text-[#b29063]">"Add to Cart"</span> to
        add them here.
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/all-products")}
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#b29063] to-[#9a7a52] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg cursor-pointer"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
        Start Shopping
      </motion.button>
    </motion.div>
  );

  // ✅ MODIFIED: ProductCard with bulk discount indicator
  const ProductCard = ({ product }) => {
    const isSelected = selectedItems.includes(product.cart_item_id);
    const basePrice = Number(product?.original_price ?? product?.price ?? 0);
    const discount = Number(
      product?.discount_price ?? product?.discounted_price ?? 0,
    );
    const hasDiscount = discount > 0 && discount < basePrice;
    const currentPrice = hasDiscount ? discount : basePrice;
    const imageUrl = getProductImage(product);
    const title = getProductTitle(product);
    const hasBulk = hasBulkDiscount(product);

    return (
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        className="group relative overflow-hidden rounded-2xl border border-[#b29063]/20 bg-white shadow-sm transition-all duration-300 hover:border-[#b29063]/30 hover:shadow-xl"
      >
        <div className="absolute right-3 top-3 z-10 flex gap-2">
          <button
            onClick={() => addToWishlist(product.product_id)}
            className="rounded-full bg-white p-2 shadow-lg hover:bg-[#b29063]/10 cursor-pointer border border-[#b29063]/20"
            title="Add to Wishlist"
          >
            <svg
              className="h-4 w-4 text-[#b29063]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            onClick={() => removeCartItem(product.cart_item_id)}
            className="rounded-full bg-white p-2 shadow-lg hover:bg-red-50 cursor-pointer border border-red-200"
            title="Remove from cart"
          >
            <svg
              className="h-4 w-4 text-red-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="absolute left-4 top-4 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleSelectItem(product.cart_item_id)}
            className="h-5 w-5 cursor-pointer rounded border-gray-300 text-[#b29063] focus:ring-[#b29063] focus:ring-offset-0"
          />
        </div>

        <div className="relative aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-[#b29063]/5 to-[#b29063]/10">
          <img
            src={imageUrl}
            alt={title}
            loading="lazy"
            className="h-full w-full object-contain transition duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = FALLBACK_IMG;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </div>

        <div className="p-4 sm:p-5">
          <h2 className="line-clamp-2 text-base font-medium text-gray-900 sm:text-lg leading-snug">
            {title}
          </h2>
          <p className="mt-1 text-xs text-gray-600">
            {getSelectionLabel(product)} • Art No: {product.art_no || "N/A"}
          </p>

<div className="mt-3 flex items-center justify-between">
  <div>
    {hasBulk ? (
      // Show bulk discount price
      <>
        <p className="text-lg font-bold text-[#b29063] sm:text-xl">
          {formatINR(product.price)}
        </p>
        <p className="text-xs text-gray-500 line-through">
          {formatINR(product.original_price)}
        </p>
        {product.applied_min_quantity && (
          <p className="text-xs text-[#b29063]">
            Bulk price (Min {product.applied_min_quantity})
          </p>
        )}
      </>
    ) : hasDiscount ? (
      // Show single discount price
      <>
        <p className="text-lg font-bold text-green-600 sm:text-xl">
          {formatINR(currentPrice)}
        </p>
        <p className="text-xs text-gray-500 line-through">
          {formatINR(basePrice)}
        </p>
      </>
    ) : (
      // Show regular price
      <p className="text-lg font-bold text-gray-900 sm:text-xl">
        {formatINR(currentPrice)}
      </p>
    )}
  </div>
  <p className="text-lg font-bold text-[#b29063] sm:text-xl">
    {formatINR(getLineTotal(product))}
  </p>
</div>

          {/* ✅ NEW: Bulk discount indicator */}
          {hasBulk && (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Bulk Discount Applied
              </span>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  handleQuantityChange(
                    product.cart_item_id,
                    (product.quantity || 1) - 1,
                  )
                }
                disabled={(product.quantity || 1) <= 1}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 cursor-pointer text-lg"
              >
                -
              </button>
              <input
                type="number"
                min="1"
                value={product.quantity || 1}
                onChange={(e) =>
                  handleQuantityChange(product.cart_item_id, e.target.value)
                }
                onBlur={(e) =>
                  handleQuantityBlur(product.cart_item_id, e.target.value)
                }
                className="h-10 w-20 rounded-lg border border-gray-300 text-center text-base focus:outline-none focus:ring-1 focus:ring-[#b29063] focus:border-[#b29063]"
                style={{ minWidth: "80px" }}
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={() =>
                  handleQuantityChange(
                    product.cart_item_id,
                    (product.quantity || 1) + 1,
                  )
                }
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer text-lg"
              >
                +
              </button>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Qty: {product.quantity || 1}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:hidden">
            <button
              onClick={() => addToWishlist(product.product_id)}
              className="flex items-center justify-center gap-2 rounded-lg border border-[#b29063]/30 bg-white px-4 py-2.5 text-sm font-medium text-[#b29063] transition-all hover:bg-[#b29063]/10 active:scale-95 cursor-pointer"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
              Wishlist
            </button>
            <button
              onClick={() => removeCartItem(product.cart_item_id)}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-medium text-red-600 transition-all hover:bg-red-50 active:scale-95 cursor-pointer"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Remove
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  const RandomProductCard = ({ product }) => {
    const cardRef = useRef(null);
    const imageUrl = getProductImage(product);

    // Helper function to get effective price with discount
    const getEffectivePrice = (product) => {
      // Check if product has pre-calculated discount info
      if (product?.has_discount && product?.discounted_price && product?.original_price) {
        return {
          final: parseFloat(product.discounted_price),
          original: parseFloat(product.original_price),
          hasDiscount: true,
          percent: product.discount_percent || 0
        };
      }

      // Check for detailed prices array
      if (product?.prices && Array.isArray(product.prices) && product.prices.length > 0) {
        let bestPrice = Infinity;
        let bestOriginal = Infinity;
        let hasDiscount = false;
        
        product.prices.forEach(priceItem => {
          const originalPrice = priceItem.price || 0;
          let finalPrice = originalPrice;
          
          if (priceItem.single_discount_type && priceItem.single_discount_value) {
            const discType = priceItem.single_discount_type;
            const discValue = parseFloat(priceItem.single_discount_value);
            
            if (discType === 'percentage') {
              finalPrice = originalPrice * (1 - discValue / 100);
            } else if (discType === 'amount' || discType === 'fixed') {
              finalPrice = originalPrice - discValue;
            }
            
            if (finalPrice < originalPrice) {
              hasDiscount = true;
            }
          }
          
          if (finalPrice < bestPrice) {
            bestPrice = finalPrice;
            bestOriginal = originalPrice;
          }
        });
        
        if (hasDiscount && bestPrice < bestOriginal) {
          return {
            final: bestPrice,
            original: bestOriginal,
            hasDiscount: true,
            percent: Math.round(((bestOriginal - bestPrice) / bestOriginal) * 100)
          };
        }
        
        return {
          final: bestPrice,
          original: bestPrice,
          hasDiscount: false,
          percent: 0
        };
      }

      // Check for string format
      if (product?.prices_string && product?.single_discount_types && product?.single_discount_values) {
        const priceArray = product.prices_string.split(',').map(p => parseFloat(p)).filter(p => !isNaN(p));
        const discountTypes = product.single_discount_types.split(',').filter(t => t !== '');
        const discountValues = product.single_discount_values.split(',').map(v => parseFloat(v)).filter(v => !isNaN(v) && v > 0);
        
        if (priceArray.length > 0) {
          let bestPrice = Infinity;
          let bestOriginal = Infinity;
          let hasDiscount = false;
          
          priceArray.forEach((price, idx) => {
            const originalPrice = price;
            let finalPrice = originalPrice;
            
            if (discountTypes[idx] && discountValues[idx] !== undefined) {
              if (discountTypes[idx] === 'percentage') {
                finalPrice = originalPrice * (1 - discountValues[idx] / 100);
              } else if (discountTypes[idx] === 'amount' || discountTypes[idx] === 'fixed') {
                finalPrice = originalPrice - discountValues[idx];
              }
              
              if (finalPrice < originalPrice) {
                hasDiscount = true;
              }
            }
            
            if (finalPrice < bestPrice) {
              bestPrice = finalPrice;
              bestOriginal = originalPrice;
            }
          });
          
          if (hasDiscount && bestPrice < bestOriginal) {
            return {
              final: bestPrice,
              original: bestOriginal,
              hasDiscount: true,
              percent: Math.round(((bestOriginal - bestPrice) / bestOriginal) * 100)
            };
          }
          
          return {
            final: bestPrice,
            original: bestPrice,
            hasDiscount: false,
            percent: 0
          };
        }
      }

      // Fallback to simple price
      const priceValue = product?.min_price || product?.price || 0;
      return {
        final: priceValue,
        original: priceValue,
        hasDiscount: false,
        percent: 0
      };
    };

    // Format price with discount display
    const formatPriceWithDiscount = (product) => {
      const priceInfo = getEffectivePrice(product);

      if (priceInfo.final === 0) {
        return "Price on request";
      }

      if (priceInfo.hasDiscount && priceInfo.final < priceInfo.original) {
        return (
          <div className="flex items-center gap-1">
            <span className="text-[#b29063] font-bold">₹{Math.round(priceInfo.final)}</span>
            <span className="text-gray-400 line-through text-[10px]">₹{Math.round(priceInfo.original)}</span>
          </div>
        );
      }

      return <span className="text-[#b29063] font-bold">₹{Math.round(priceInfo.final)}</span>;
    };

    // Get discount badge
    const getDiscountBadge = (product) => {
      const priceInfo = getEffectivePrice(product);
      
      if (priceInfo.hasDiscount && priceInfo.percent > 0) {
        return (
          <span className="absolute top-2 left-2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
            {priceInfo.percent}% OFF
          </span>
        );
      }
      
      return null;
    };

    const onCardEnter = () => {
      if (cardRef.current) {
        gsap.to(cardRef.current, {
          y: -10,
          boxShadow: "0px 45px 60px 0px rgba(178, 144, 99, 0.1)",
          duration: 0.22,
          ease: "power2.out",
        });
      }
    };

    const onCardLeave = () => {
      if (cardRef.current) {
        gsap.to(cardRef.current, {
          y: 0,
          boxShadow: "0px 15px 20px 0px rgba(178, 144, 99, 0.05)",
          duration: 0.22,
          ease: "power2.out",
        });
      }
    };

    const title = getProductTitle(product);
    const productId = product?.product_id || product?.id;
    const priceDisplay = formatPriceWithDiscount(product);
    const discountBadge = getDiscountBadge(product);

    return (
      <div
        ref={cardRef}
        className="relative flex flex-col bg-white p-2 shadow-[0px_15px_20px_0px_rgba(178,144,99,0.05)] will-change-transform hover:shadow-lg transition-shadow duration-200 border border-[#b29063]/10 cursor-pointer"
        onMouseEnter={onCardEnter}
        onMouseLeave={onCardLeave}
        onClick={() => viewProductDetails(productId)}
      >
        <div className="relative w-full">
          <div className="absolute left-0 top-0 z-[2] flex w-full items-center justify-between p-2 text-[11px] tracking-[0.1em] text-gray-700 bg-white/80 backdrop-blur-sm border-b border-[#b29063]/10">
            <span className="uppercase truncate max-w-[55%] font-semibold text-gray-900">
              {product.category_name || product.category || "Product"}
            </span>
            <span className="uppercase font-bold text-[#b29063]">
              {priceDisplay}
            </span>
          </div>
          <div className="w-full h-40 flex items-center justify-center bg-gradient-to-br from-[#b29063]/5 to-[#b29063]/10 mt-8 relative">
            {discountBadge}
            <img
              src={imageUrl}
              alt={title}
              className="block max-h-[140px] w-full max-w-full object-contain"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>
        </div>
        <div className="mt-0 px-2 pt-1 pb-2">
          <p className="line-clamp-1 text-[12px] font-semibold tracking-[0.06em] text-gray-900">
            {title}
          </p>
          <p className="mt-0 line-clamp-2 text-[10px] tracking-[0.08em] text-gray-600">
            {product.description ||
              "No description available"}
          </p>
        </div>
      </div>
    );
  };


  useEffect(() => {
    if (isLoading || cart.length === 0) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".cart-page-heading",
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.75, ease: "power3.out" }
      );

      gsap.fromTo(
        ".cart-row-reveal",
        { opacity: 0, y: 42 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cart-products-panel",
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ".cart-summary-reveal",
        { opacity: 0, x: 42 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cart-summary-column",
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ".cart-service-reveal",
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cart-services",
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });

    const refresh = window.setTimeout(() => ScrollTrigger.refresh(), 250);

    return () => {
      window.clearTimeout(refresh);
      ctx.revert();
    };
  }, [isLoading, cart.length]);

  const shippingAmount = 0;
  const displayedSubtotal = selectedItems.length ? selectedTotalAmount : totalAmount;
  const displayedDiscount = Number(couponDiscount || 0);
  const displayedFinalAmount = Math.max(
    0,
    (selectedItems.length ? finalAmount : totalAmount - displayedDiscount) +
      shippingAmount
  );

  const CartIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 4h2l2.1 10.1a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L20 7H6" />
      <circle cx="10" cy="20" r="1.2" />
      <circle cx="18" cy="20" r="1.2" />
    </svg>
  );

  const TrashIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13" />
      <path d="M10 11v5M14 11v5" />
    </svg>
  );

  const TruckIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 5h11v11H3zM14 9h4l3 3v4h-7z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
    </svg>
  );

  const ReturnIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 10a8 8 0 1 1 2 8" />
      <path d="M4 4v6h6" />
    </svg>
  );

  const ShieldIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 3l8 3v6c0 5-3.4 8.2-8 10-4.6-1.8-8-5-8-10V6l8-3z" />
      <path d="M8.5 12l2.2 2.2 4.8-5" />
    </svg>
  );

  const LockIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );

  const CouponIcon = ({ className = "" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M20 12l-8 8-9-9V4h7l10 8z" />
      <circle cx="7.5" cy="8.5" r="1.2" />
    </svg>
  );

  const ServiceItem = ({ icon: Icon, title, text }) => (
    <div className="cart-service-reveal cart-service-item">
      <Icon className="cart-service-icon" />
      <div>
        <strong>{title}</strong>
        <span>{text}</span>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <section className="min-h-screen bg-[var(--user-bg,#E8D9D5)] font-['Poppins',sans-serif]">
        <div className="mx-auto max-w-7xl px-3 py-10 sm:px-4 lg:px-6">
          <LoadingState />
        </div>
      </section>
    );
  }

  if (cart.length === 0 && !isLoading) {
    return (
      <section className="min-h-screen bg-[var(--user-bg,#E8D9D5)] font-['Poppins',sans-serif]">
        <div className="mx-auto max-w-7xl px-3 py-10 sm:px-4 lg:px-6">
          <EmptyState />
        </div>
      </section>
    );
  }

  return (
    <section className="cart-premium-page">
      <style>{`
        .cart-premium-page,
        .cart-premium-page * {
          box-sizing: border-box;
        }

        .cart-premium-page {
          --cart-maroon: var(--user-maroon, #2b0000);
          --cart-maroon-dark: var(--user-maroon-deep, #1b0000);
          --cart-gold: var(--user-gold, #c99945);
          --cart-gold-soft: var(--user-gold-soft, #e2bd73);
          --cart-paper: var(--user-card, #fffaf4);
          --cart-line: var(--user-border, rgba(96, 39, 23, 0.15));
          min-height: 100vh;
          overflow-x: hidden;
          background: var(--user-bg, #E8D9D5);
          color: var(--user-text, #2d1510);
          font-family: "Poppins", Arial, sans-serif;
          padding: 42px 20px 70px;
        }

        .cart-shell {
          width: min(1450px, 100%);
          margin: 0 auto;
        }

        .cart-page-heading {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 24px;
          margin-bottom: 28px;
        }

        .cart-page-heading h1 {
          margin: 0;
          color: var(--user-text, #30100d);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(40px, 4vw, 62px);
          font-weight: 400;
          line-height: 1;
        }

        .cart-page-heading h1 span {
          font-family: "Poppins", Arial, sans-serif;
          font-size: 0.48em;
          font-weight: 500;
        }

        .cart-page-heading p {
          margin: 14px 0 0;
          color: var(--user-text-soft, #5f4c46);
          font-size: 15px;
        }

        .continue-shopping-btn {
          display: inline-flex;
          min-width: 220px;
          align-items: center;
          justify-content: center;
          gap: 12px;
          border: 1px solid var(--cart-gold);
          border-radius: 11px;
          background: rgba(255, 250, 244, 0.72);
          padding: 15px 24px;
          color: #34110d;
          font-size: 14px;
          font-weight: 500;
          transition: background .25s ease, color .25s ease, transform .25s ease;
        }

        .continue-shopping-btn:hover {
          background: var(--cart-maroon);
          color: #fff8ed;
          transform: translateY(-2px);
        }

        .cart-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 330px;
          gap: 20px;
          align-items: start;
        }

        .cart-products-panel,
        .cart-summary-card,
        .cart-coupon-card,
        .cart-payment-card,
        .cart-services {
          border: 1px solid var(--cart-line);
          border-radius: 13px;
          background:
            radial-gradient(circle at 100% 0%, rgba(201,153,69,.06), transparent 35%),
            rgba(255,250,244,.82);
          box-shadow: 0 14px 35px rgba(70, 20, 10, 0.045);
        }

        .cart-products-panel {
          overflow: hidden;
        }

        .cart-table-head {
          display: grid;
          grid-template-columns: minmax(310px, 1fr) 150px 180px 145px 42px;
          align-items: center;
          min-height: 64px;
          padding: 0 22px;
          border-bottom: 1px solid var(--cart-line);
          color: #3d201b;
          font-size: 12px;
          text-transform: uppercase;
        }

        .cart-table-head span:nth-child(n+2) {
          text-align: center;
        }

        .cart-desktop-row {
          display: grid;
          grid-template-columns: minmax(310px, 1fr) 150px 180px 145px 42px;
          align-items: center;
          min-height: 157px;
          padding: 20px 22px;
          border-bottom: 1px solid var(--cart-line);
        }

        .cart-desktop-row:last-child {
          border-bottom: 0;
        }

        .cart-product-info {
          display: grid;
          grid-template-columns: 22px 145px minmax(0, 1fr);
          gap: 17px;
          align-items: center;
          min-width: 0;
        }

        .cart-select-checkbox {
          width: 17px;
          height: 17px;
          accent-color: var(--cart-maroon);
          cursor: pointer;
        }

        .cart-product-image {
          width: 145px;
          height: 112px;
          overflow: hidden;
          border-radius: 9px;
          background: #f7ede1;
        }

        .cart-product-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform .45s ease;
        }

        .cart-desktop-row:hover .cart-product-image img,
        .cart-mobile-item:hover .cart-product-image img {
          transform: scale(1.06);
        }

        .cart-product-copy {
          min-width: 0;
        }

        .cart-product-copy h2 {
          margin: 0 0 7px;
          color: #32120f;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 21px;
          font-weight: 500;
          line-height: 1.2;
        }

        .cart-product-copy p {
          margin: 3px 0;
          color: #604b44;
          font-size: 13px;
        }

        .cart-bulk-badge {
          display: inline-flex;
          margin-top: 7px;
          border-radius: 999px;
          background: rgba(201,153,69,.14);
          padding: 4px 9px;
          color: #795419;
          font-size: 10px;
          font-weight: 600;
        }

        .cart-price-cell,
        .cart-total-cell {
          text-align: center;
          color: #32120f;
          font-size: 16px;
          font-weight: 600;
        }

        .cart-price-cell s {
          display: block;
          margin-top: 3px;
          color: #9a8178;
          font-size: 11px;
          font-weight: 400;
        }

        .cart-qty {
          display: grid;
          grid-template-columns: 40px 50px 40px;
          width: max-content;
          margin: 0 auto;
          overflow: hidden;
          border: 1px solid rgba(201,153,69,.45);
          border-radius: 8px;
          background: rgba(255,255,255,.55);
        }

        .cart-qty button,
        .cart-qty input {
          height: 43px;
          border: 0;
          background: transparent;
          color: #3a1712;
          text-align: center;
        }

        .cart-qty button {
          cursor: pointer;
          font-size: 19px;
          transition: background .2s ease;
        }

        .cart-qty button:hover {
          background: rgba(201,153,69,.12);
        }

        .cart-qty input {
          width: 100%;
          border-right: 1px solid rgba(201,153,69,.25);
          border-left: 1px solid rgba(201,153,69,.25);
          outline: none;
          font-size: 14px;
          -moz-appearance: textfield;
        }

        .cart-qty input::-webkit-outer-spin-button,
        .cart-qty input::-webkit-inner-spin-button {
          margin: 0;
          -webkit-appearance: none;
        }

        .cart-remove-btn {
          display: grid;
          width: 38px;
          height: 38px;
          place-items: center;
          border: 1px solid rgba(201,153,69,.28);
          border-radius: 50%;
          background: rgba(255,255,255,.55);
          color: #6b301f;
          cursor: pointer;
          transition: background .22s ease, color .22s ease, transform .22s ease;
        }

        .cart-remove-btn:hover {
          background: var(--cart-maroon);
          color: #fff;
          transform: translateY(-2px);
        }

        .cart-remove-btn svg {
          width: 17px;
          height: 17px;
        }

        .cart-mobile-list {
          display: none;
        }

        .cart-summary-column {
          display: grid;
          gap: 17px;
          position: sticky;
          top: 100px;
        }

        .cart-summary-card {
          padding: 25px 20px 20px;
        }

        .cart-summary-card h2 {
          margin: 0 0 22px;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 26px;
          font-weight: 500;
        }

        .summary-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          margin: 13px 0;
          color: #5b423a;
          font-size: 13px;
        }

        .summary-row strong {
          color: #2f130f;
          font-weight: 500;
        }

        .summary-row .free {
          color: #22842d;
          font-weight: 600;
        }

        .summary-row .discount {
          color: #c11e1e;
        }

        .summary-rule {
          height: 1px;
          margin: 20px 0;
          background: var(--cart-line);
        }

        .summary-total {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .summary-total strong {
          font-family: Georgia, "Times New Roman", serif;
          font-size: 17px;
          font-weight: 500;
        }

        .summary-total b {
          color: #34100d;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 27px;
          font-weight: 500;
        }

        .summary-tax {
          margin: 6px 0 20px;
          color: #806b64;
          font-size: 11px;
        }

        .checkout-btn,
        .buy-now-btn {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 52px;
          border-radius: 7px;
          cursor: pointer;
          font-size: 15px;
          font-weight: 500;
          transition: transform .22s ease, box-shadow .22s ease;
        }

        .checkout-btn {
          border: 1px solid var(--cart-maroon);
          background: linear-gradient(135deg, #420000, #220000);
          color: #f4d68f;
          box-shadow: 0 10px 24px rgba(43,0,0,.15);
        }

        .checkout-btn:hover,
        .buy-now-btn:hover {
          transform: translateY(-2px);
        }

        .checkout-btn svg {
          width: 19px;
          height: 19px;
        }

        .summary-or {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 15px 0;
          color: #8c7168;
          font-size: 11px;
        }

        .summary-or::before,
        .summary-or::after {
          content: "";
          flex: 1;
          height: 1px;
          background: var(--cart-line);
        }

        .buy-now-btn {
          border: 1px solid var(--cart-gold);
          background: transparent;
          color: #a36d17;
        }

        .cart-coupon-card {
          padding: 18px;
        }

        .coupon-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 13px;
          color: #3b211b;
          font-size: 12px;
        }

        .coupon-title svg {
          width: 25px;
          height: 25px;
        }

        .coupon-entry {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 8px;
        }

        .coupon-entry input {
          min-width: 0;
          height: 43px;
          border: 1px solid var(--cart-line);
          border-radius: 7px;
          background: rgba(255,255,255,.55);
          padding: 0 12px;
          outline: none;
          color: #3c201a;
          font-size: 12px;
        }

        .coupon-entry input:focus {
          border-color: var(--cart-gold);
        }

        .coupon-entry button {
          min-width: 75px;
          border: 1px solid rgba(201,153,69,.45);
          border-radius: 7px;
          background: transparent;
          color: #3f1b15;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
        }

        .coupon-list {
          display: grid;
          gap: 8px;
          margin-top: 13px;
        }

        .coupon-list-label {
          margin: 0;
          color: #6f554c;
          font-size: 11px;
          font-weight: 500;
        }

        .coupon-option {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 10px;
          width: 100%;
          border: 1px dashed rgba(201,153,69,.5);
          border-radius: 8px;
          background: rgba(255,255,255,.42);
          padding: 9px 10px;
          color: #3c201a;
          text-align: left;
          cursor: pointer;
          transition: border-color .2s ease, background .2s ease, transform .2s ease;
        }

        .coupon-option:hover {
          border-color: var(--cart-gold);
          background: rgba(255,255,255,.68);
          transform: translateY(-1px);
        }

        .coupon-option-code {
          display: block;
          color: #350f0b;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: .04em;
        }

        .coupon-option-text {
          display: block;
          margin-top: 3px;
          color: #796259;
          font-size: 10px;
          line-height: 1.35;
        }

        .coupon-option-apply {
          border-radius: 999px;
          background: rgba(201,153,69,.13);
          padding: 5px 9px;
          color: #8c5d12;
          font-size: 10px;
          font-weight: 700;
        }

        .coupon-list-empty {
          margin: 11px 0 0;
          color: #806b64;
          font-size: 11px;
        }

        .coupon-applied {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          border: 1px solid rgba(40,135,54,.25);
          border-radius: 8px;
          background: rgba(40,135,54,.07);
          padding: 11px;
          font-size: 12px;
        }

        .coupon-applied button {
          border: 0;
          background: transparent;
          color: #a31d1d;
          cursor: pointer;
          font-weight: 600;
        }

        .cart-payment-card {
          padding: 20px 14px;
          text-align: center;
        }

        .cart-payment-card p {
          margin: 0 0 18px;
          color: #3e211b;
          font-size: 12px;
        }

        .payment-logos {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
          color: #28406c;
          font-size: 13px;
          font-weight: 800;
        }

        .payment-logos span:nth-child(2) {
          color: #d04b32;
        }

        .payment-logos span:nth-child(4) {
          color: #111;
        }

        .cart-services {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          margin-top: 22px;
          padding: 27px 22px;
        }

        .cart-service-item {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
          min-height: 52px;
          padding: 0 22px;
          border-right: 1px solid var(--cart-line);
        }

        .cart-service-item:last-child {
          border-right: 0;
        }

        .cart-service-icon {
          width: 34px;
          height: 34px;
          flex: none;
          color: #a96f26;
        }

        .cart-service-item strong,
        .cart-service-item span {
          display: block;
        }

        .cart-service-item strong {
          color: #3e1d17;
          font-size: 13px;
          font-weight: 600;
        }

        .cart-service-item span {
          margin-top: 4px;
          color: #715e57;
          font-size: 11px;
        }

        .cart-toast {
          position: fixed;
          z-index: 2000;
          right: 22px;
          bottom: 22px;
          display: flex;
          max-width: 390px;
          align-items: center;
          gap: 12px;
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 12px;
          background: #2b0000;
          padding: 15px 18px;
          color: #fff;
          box-shadow: 0 20px 50px rgba(43,0,0,.28);
        }

        .cart-toast button {
          border: 0;
          background: transparent;
          color: #fff;
          cursor: pointer;
          font-size: 20px;
        }

        @media (max-width: 1050px) {
          .cart-layout {
            grid-template-columns: 1fr 300px;
          }

          .cart-table-head,
          .cart-desktop-row {
            grid-template-columns: minmax(270px, 1fr) 125px 145px 125px 38px;
          }

          .cart-product-info {
            grid-template-columns: 20px 110px minmax(0, 1fr);
          }

          .cart-product-image {
            width: 110px;
            height: 95px;
          }
        }

        @media (max-width: 767px) {
          .cart-premium-page {
            padding: 25px 10px 45px;
          }

          .cart-shell {
            width: min(480px, 100%);
          }

          .cart-page-heading {
            display: block;
            margin-bottom: 13px;
          }

          .cart-page-heading h1 {
            font-size: 31px;
          }

          .cart-page-heading h1 span {
            font-size: .55em;
          }

          .cart-page-heading p {
            margin-top: 8px;
            font-size: 12px;
          }

          .continue-shopping-btn {
            width: 100%;
            min-width: 0;
            margin-top: 16px;
            min-height: 45px;
            padding: 10px 15px;
            font-size: 11px;
          }

          .cart-layout {
            display: block;
          }

          .cart-products-panel {
            background: transparent;
            border: 0;
            border-radius: 0;
            box-shadow: none;
          }

          .cart-table-head,
          .cart-desktop-list {
            display: none;
          }

          .cart-mobile-list {
            display: grid;
            gap: 10px;
          }

          .cart-mobile-item {
            display: grid;
            grid-template-columns: 92px minmax(0, 1fr);
            gap: 12px;
            position: relative;
            min-height: 142px;
            border: 1px solid var(--cart-line);
            border-radius: 10px;
            background: rgba(255,250,244,.82);
            padding: 10px;
          }

          .cart-mobile-item .cart-product-image {
            width: 92px;
            height: 92px;
            align-self: start;
          }

          .cart-mobile-content {
            min-width: 0;
            padding-right: 29px;
          }

          .cart-mobile-content h2 {
            margin: 2px 0 5px;
            color: #30120e;
            font-family: Georgia, "Times New Roman", serif;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.22;
          }

          .cart-mobile-content p {
            margin: 2px 0;
            color: #68534c;
            font-size: 10px;
          }

          .cart-mobile-remove {
            position: absolute;
            top: 9px;
            right: 8px;
            width: 27px;
            height: 27px;
          }

          .cart-mobile-remove svg {
            width: 13px;
            height: 13px;
          }

          .cart-mobile-bottom {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 8px;
            margin-top: 10px;
          }

          .cart-mobile-item .cart-qty {
            grid-template-columns: 27px 34px 27px;
          }

          .cart-mobile-item .cart-qty button,
          .cart-mobile-item .cart-qty input {
            height: 28px;
            font-size: 11px;
          }

          .cart-mobile-price {
            color: #31110d;
            font-size: 12px;
            font-weight: 700;
            white-space: nowrap;
          }

          .cart-mobile-select {
            position: absolute;
            left: 15px;
            top: 14px;
            z-index: 3;
          }

          .cart-summary-column {
            position: static;
            margin-top: 13px;
            gap: 10px;
          }

          .cart-summary-card,
          .cart-coupon-card,
          .cart-payment-card {
            border-radius: 10px;
          }

          .cart-summary-card {
            padding: 15px 14px 13px;
          }

          .cart-summary-card h2 {
            display: none;
          }

          .summary-row {
            margin: 9px 0;
            font-size: 10px;
          }

          .summary-rule {
            margin: 12px 0;
          }

          .summary-total strong {
            font-size: 13px;
          }

          .summary-total b {
            font-size: 23px;
          }

          .summary-tax {
            margin-bottom: 12px;
            font-size: 9px;
          }

          .checkout-btn,
          .buy-now-btn {
            min-height: 40px;
            font-size: 11px;
          }

          .summary-or {
            margin: 10px 0;
            font-size: 9px;
          }

          .cart-coupon-card {
            padding: 13px;
          }

          .coupon-title {
            margin-bottom: 10px;
            font-size: 9px;
          }

          .coupon-title svg {
            width: 20px;
            height: 20px;
          }

          .coupon-entry input {
            height: 36px;
            font-size: 9px;
          }

          .coupon-entry button {
            min-width: 57px;
            font-size: 9px;
          }

          .cart-payment-card {
            padding: 15px 10px;
          }

          .cart-payment-card p {
            margin-bottom: 13px;
            font-size: 10px;
          }

          .payment-logos {
            gap: 15px;
            font-size: 11px;
          }

          .cart-services {
            display: none;
          }

          .cart-toast {
            right: 10px;
            bottom: 10px;
            left: 10px;
            max-width: none;
            font-size: 12px;
          }
        }
      `}</style>

      <div className="cart-shell">
        <header className="cart-page-heading">
          <div>
            <h1>
              Your Cart <span>({cart.length})</span>
            </h1>
            <p>Review your items and proceed to secure checkout.</p>
          </div>

          <button
            type="button"
            className="continue-shopping-btn"
            onClick={() => navigate("/all-products")}
          >
            <span aria-hidden="true">←</span>
            Continue Shopping
          </button>
        </header>

        <div className="cart-layout">
          <div>
            <div className="cart-products-panel">
              <div className="cart-table-head">
                <span>Product</span>
                <span>Price</span>
                <span>Quantity</span>
                <span>Total</span>
                <span />
              </div>

              <div className="cart-desktop-list">
                {cart.map((product) => {
                  const imageUrl = getProductImage(product);
                  const title = getProductTitle(product);
                  const unitPrice = getUnitPrice(product);
                  const originalPrice = Number(product?.original_price || 0);
                  const showOriginal =
                    originalPrice > 0 && originalPrice > Number(unitPrice || 0);
                  const hasBulk = hasBulkDiscount(product);

                  return (
                    <article
                      className="cart-desktop-row cart-row-reveal"
                      key={product.cart_item_id}
                    >
                      <div className="cart-product-info">
                        <input
                          type="checkbox"
                          className="cart-select-checkbox"
                          checked={selectedItems.includes(product.cart_item_id)}
                          onChange={() => handleSelectItem(product.cart_item_id)}
                          aria-label={`Select ${title}`}
                        />

                        <div className="cart-product-image">
                          <img
                            src={imageUrl}
                            alt={title}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = FALLBACK_IMG;
                            }}
                          />
                        </div>

                        <div className="cart-product-copy">
                          <h2>{title}</h2>
                          <p>
                            {product.purity || product.metal || "Gold Jewellery"}
                          </p>
                          <p>{getSelectionLabel(product)}</p>

                          {hasBulk && (
                            <span className="cart-bulk-badge">
                              Bulk Discount Applied
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cart-price-cell">
                        {formatINR(unitPrice)}
                        {showOriginal && <s>{formatINR(originalPrice)}</s>}
                      </div>

                      <div>
                        <div className="cart-qty">
                          <button
                            type="button"
                            disabled={
                              (Number(product.quantity) || 1) <= 1 ||
                              updatingItemId === product.cart_item_id
                            }
                            onClick={() =>
                              handleQuantityChange(
                                product.cart_item_id,
                                (Number(product.quantity) || 1) - 1
                              )
                            }
                          >
                            −
                          </button>

                          <input
                            type="number"
                            min="1"
                            value={product.quantity || 1}
                            disabled={updatingItemId === product.cart_item_id}
                            onChange={(e) =>
                              handleQuantityChange(
                                product.cart_item_id,
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleQuantityBlur(
                                product.cart_item_id,
                                e.target.value
                              )
                            }
                          />

                          <button
                            type="button"
                            disabled={updatingItemId === product.cart_item_id}
                            onClick={() =>
                              handleQuantityChange(
                                product.cart_item_id,
                                (Number(product.quantity) || 1) + 1
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="cart-total-cell">
                        {formatINR(getLineTotal(product))}
                      </div>

                      <button
                        type="button"
                        className="cart-remove-btn"
                        onClick={() => removeCartItem(product.cart_item_id)}
                        aria-label={`Remove ${title}`}
                      >
                        <TrashIcon />
                      </button>
                    </article>
                  );
                })}
              </div>

              <div className="cart-mobile-list">
                {cart.map((product) => {
                  const imageUrl = getProductImage(product);
                  const title = getProductTitle(product);

                  return (
                    <article
                      className="cart-mobile-item cart-row-reveal"
                      key={product.cart_item_id}
                    >
                      <input
                        type="checkbox"
                        className="cart-select-checkbox cart-mobile-select"
                        checked={selectedItems.includes(product.cart_item_id)}
                        onChange={() => handleSelectItem(product.cart_item_id)}
                        aria-label={`Select ${title}`}
                      />

                      <div className="cart-product-image">
                        <img
                          src={imageUrl}
                          alt={title}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = FALLBACK_IMG;
                          }}
                        />
                      </div>

                      <div className="cart-mobile-content">
                        <h2>{title}</h2>
                        <p>{product.purity || product.metal || "Gold Jewellery"}</p>
                        <p>{getSelectionLabel(product)}</p>

                        <div className="cart-mobile-bottom">
                          <div className="cart-qty">
                            <button
                              type="button"
                              disabled={
                                (Number(product.quantity) || 1) <= 1 ||
                                updatingItemId === product.cart_item_id
                              }
                              onClick={() =>
                                handleQuantityChange(
                                  product.cart_item_id,
                                  (Number(product.quantity) || 1) - 1
                                )
                              }
                            >
                              −
                            </button>

                            <input
                              type="number"
                              min="1"
                              value={product.quantity || 1}
                              disabled={updatingItemId === product.cart_item_id}
                              onChange={(e) =>
                                handleQuantityChange(
                                  product.cart_item_id,
                                  e.target.value
                                )
                              }
                              onBlur={(e) =>
                                handleQuantityBlur(
                                  product.cart_item_id,
                                  e.target.value
                                )
                              }
                            />

                            <button
                              type="button"
                              disabled={updatingItemId === product.cart_item_id}
                              onClick={() =>
                                handleQuantityChange(
                                  product.cart_item_id,
                                  (Number(product.quantity) || 1) + 1
                                )
                              }
                            >
                              +
                            </button>
                          </div>

                          <strong className="cart-mobile-price">
                            {formatINR(getLineTotal(product))}
                          </strong>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="cart-remove-btn cart-mobile-remove"
                        onClick={() => removeCartItem(product.cart_item_id)}
                        aria-label={`Remove ${title}`}
                      >
                        <TrashIcon />
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="cart-services">
              <ServiceItem
                icon={TruckIcon}
                title="Free Shipping"
                text="On orders above ₹20,000"
              />
              <ServiceItem
                icon={ReturnIcon}
                title="Easy Returns"
                text="7 days return policy"
              />
              <ServiceItem
                icon={ShieldIcon}
                title="Certified Jewellery"
                text="100% Hallmarked"
              />
              <ServiceItem
                icon={LockIcon}
                title="Secure Payments"
                text="100% safe & secure"
              />
            </div>
          </div>

          <aside className="cart-summary-column">
            <div className="cart-summary-card cart-summary-reveal">
              <h2>Order Summary</h2>

              <div className="summary-row">
                <span>Subtotal ({selectedItems.length || cart.length} Items)</span>
                <strong>{formatINR(displayedSubtotal)}</strong>
              </div>

              <div className="summary-row">
                <span>Shipping</span>
                <strong className="free">FREE</strong>
              </div>

              <div className="summary-row">
                <span>Discount</span>
                <strong className="discount">
                  −{formatINR(displayedDiscount)}
                </strong>
              </div>

              <div className="summary-rule" />

              <div className="summary-total">
                <strong>Total Amount</strong>
                <b>{formatINR(displayedFinalAmount)}</b>
              </div>

              <p className="summary-tax">(Inclusive of all taxes)</p>

              <button
                type="button"
                className="checkout-btn"
                onClick={handleProceedToCheckout}
              >
                <LockIcon />
                Proceed to Checkout
              </button>

              <div className="summary-or">or</div>

              <button
                type="button"
                className="buy-now-btn"
                onClick={handleProceedToCheckout}
              >
                Buy Now
              </button>
            </div>

            <div className="cart-coupon-card cart-summary-reveal">
              <div className="coupon-title">
                <CouponIcon />
                <span>Have a coupon code?</span>
              </div>

              {appliedCoupon ? (
                <div className="coupon-applied">
                  <span>
                    <strong>{appliedCoupon.code}</strong> applied
                  </span>
                  <button type="button" onClick={removeCoupon}>
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div className="coupon-entry">
                    <input
                      ref={couponInputRef}
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") applyCoupon();
                      }}
                      placeholder="Enter coupon code"
                      aria-label="Coupon code"
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      disabled={isApplyingCoupon}
                    >
                      {isApplyingCoupon ? "..." : "Apply"}
                    </button>
                  </div>

                  {loadingCoupons ? (
                    <p className="coupon-list-empty">Loading public coupons...</p>
                  ) : availableCoupons.length > 0 ? (
                    <div className="coupon-list" aria-label="Available public coupons">
                      <p className="coupon-list-label">Available coupons</p>
                      {availableCoupons.map((coupon) => (
                        <button
                          key={coupon.id || coupon.code}
                          type="button"
                          className="coupon-option"
                          onClick={() => {
                            setCouponCode(String(coupon.code || "").toUpperCase());
                            window.setTimeout(() => couponInputRef.current?.focus(), 0);
                          }}
                        >
                          <span>
                            <span className="coupon-option-code">{coupon.code}</span>
                            <span className="coupon-option-text">
                              {coupon.description || formatCouponOffer(coupon)}
                            </span>
                          </span>
                          <span className="coupon-option-apply">Use</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="coupon-list-empty">No public coupons available right now.</p>
                  )}
                </>
              )}
            </div>

            <div className="cart-payment-card cart-summary-reveal">
              <p>We Accept</p>
              <div className="payment-logos" aria-label="Accepted payment methods">
                <span>VISA</span>
                <span>●●</span>
                <span>AMEX</span>
                <span>UPI</span>
                <span>paytm</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <AnimatePresence>
        {message && (
          <motion.div
            ref={toastRef}
            className="cart-toast"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 18 }}
          >
            <span>{message}</span>
            <button
              type="button"
              onClick={() => setMessage("")}
              aria-label="Close notification"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default Cart;
